import React, { useState, useEffect } from "react";

const SEOSettingsForm = ({ seoSetting, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    pagePath: "",
    title: "",
    description: "",
    keywords: "",
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    ogType: "website",
    ogUrl: "",
    twitterCard: "summary_large_image",
    twitterTitle: "",
    twitterDescription: "",
    twitterImage: "",
    canonicalUrl: "",
    robots: "index, follow",
    author: "",
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (seoSetting) {
      setFormData({
        pagePath: seoSetting.pagePath || "",
        title: seoSetting.title || "",
        description: seoSetting.description || "",
        keywords: seoSetting.keywords || "",
        ogTitle: seoSetting.ogTitle || "",
        ogDescription: seoSetting.ogDescription || "",
        ogImage: seoSetting.ogImage || "",
        ogType: seoSetting.ogType || "website",
        ogUrl: seoSetting.ogUrl || "",
        twitterCard: seoSetting.twitterCard || "summary_large_image",
        twitterTitle: seoSetting.twitterTitle || "",
        twitterDescription: seoSetting.twitterDescription || "",
        twitterImage: seoSetting.twitterImage || "",
        canonicalUrl: seoSetting.canonicalUrl || "",
        robots: seoSetting.robots || "index, follow",
        author: seoSetting.author || "",
        isActive:
          seoSetting.isActive !== undefined ? seoSetting.isActive : true,
      });
    }
  }, [seoSetting]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.pagePath.trim()) {
      newErrors.pagePath = "Page path is required";
    }

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.length > 1000) {
      newErrors.description = "Description should be 1000 characters or less";
    }

    if (!formData.keywords.trim()) {
      newErrors.keywords = "Keywords are required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error("Error saving SEO settings:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateFromTitle = () => {
    if (formData.title && !formData.ogTitle) {
      setFormData((prev) => ({
        ...prev,
        ogTitle: formData.title,
      }));
    }
    if (formData.description && !formData.ogDescription) {
      setFormData((prev) => ({
        ...prev,
        ogDescription: formData.description,
      }));
    }
    if (formData.title && !formData.twitterTitle) {
      setFormData((prev) => ({
        ...prev,
        twitterTitle: formData.title,
      }));
    }
    if (formData.description && !formData.twitterDescription) {
      setFormData((prev) => ({
        ...prev,
        twitterDescription: formData.description,
      }));
    }
  };

  return (
    <div className="seo-settings-form">
      <div className="form-header">
        <h2>{seoSetting ? "Edit SEO Settings" : "Create New SEO Settings"}</h2>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleGenerateFromTitle}
        >
          Auto-fill Social Media
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Basic SEO Settings */}
          <div className="col-md-6">
            <div className="form-section">
              <h3>Basic SEO Settings</h3>

              <div className="mb-3">
                <label htmlFor="pagePath" className="form-label">
                  Page Path *
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    errors.pagePath ? "is-invalid" : ""
                  }`}
                  id="pagePath"
                  name="pagePath"
                  value={formData.pagePath}
                  onChange={handleInputChange}
                  placeholder="/about"
                />
                {errors.pagePath && (
                  <div className="invalid-feedback">{errors.pagePath}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="title" className="form-label">
                  Page Title *
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Page Title | Polani Travels"
                />
                {errors.title && (
                  <div className="invalid-feedback">{errors.title}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="description" className="form-label">
                  Meta Description *
                </label>
                <textarea
                  className={`form-control ${
                    errors.description ? "is-invalid" : ""
                  }`}
                  id="description"
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter meta description (max 1000 characters)"
                />
                <div className="form-text">
                  {formData.description.length}/1000 characters
                </div>
                {errors.description && (
                  <div className="invalid-feedback">{errors.description}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="keywords" className="form-label">
                  Keywords *
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    errors.keywords ? "is-invalid" : ""
                  }`}
                  id="keywords"
                  name="keywords"
                  value={formData.keywords}
                  onChange={handleInputChange}
                  placeholder="keyword1, keyword2, keyword3"
                />
                {errors.keywords && (
                  <div className="invalid-feedback">{errors.keywords}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="canonicalUrl" className="form-label">
                  Canonical URL
                </label>
                <input
                  type="url"
                  className="form-control"
                  id="canonicalUrl"
                  name="canonicalUrl"
                  value={formData.canonicalUrl}
                  onChange={handleInputChange}
                  placeholder="https://polani-2c215.web.app/page-path"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="robots" className="form-label">
                  Robots Meta
                </label>
                <select
                  className="form-select"
                  id="robots"
                  name="robots"
                  value={formData.robots}
                  onChange={handleInputChange}
                >
                  <option value="index, follow">Index, Follow</option>
                  <option value="noindex, follow">No Index, Follow</option>
                  <option value="index, nofollow">Index, No Follow</option>
                  <option value="noindex, nofollow">No Index, No Follow</option>
                </select>
              </div>

              <div className="mb-3">
                <label htmlFor="author" className="form-label">
                  Author
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="author"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="Polani Travels"
                />
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Active
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Social Media Settings */}
          <div className="col-md-6">
            <div className="form-section">
              <h3>Open Graph (Facebook)</h3>

              <div className="mb-3">
                <label htmlFor="ogTitle" className="form-label">
                  OG Title
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="ogTitle"
                  name="ogTitle"
                  value={formData.ogTitle}
                  onChange={handleInputChange}
                  placeholder="Open Graph Title"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="ogDescription" className="form-label">
                  OG Description
                </label>
                <textarea
                  className="form-control"
                  id="ogDescription"
                  name="ogDescription"
                  rows="3"
                  value={formData.ogDescription}
                  onChange={handleInputChange}
                  placeholder="Open Graph Description"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="ogImage" className="form-label">
                  OG Image URL
                </label>
                <input
                  type="url"
                  className="form-control"
                  id="ogImage"
                  name="ogImage"
                  value={formData.ogImage}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="ogType" className="form-label">
                  OG Type
                </label>
                <select
                  className="form-select"
                  id="ogType"
                  name="ogType"
                  value={formData.ogType}
                  onChange={handleInputChange}
                >
                  <option value="website">Website</option>
                  <option value="article">Article</option>
                  <option value="product">Product</option>
                </select>
              </div>

              <div className="mb-3">
                <label htmlFor="ogUrl" className="form-label">
                  OG URL
                </label>
                <input
                  type="url"
                  className="form-control"
                  id="ogUrl"
                  name="ogUrl"
                  value={formData.ogUrl}
                  onChange={handleInputChange}
                  placeholder="https://polani-2c215.web.app/page-path"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Twitter Card</h3>

              <div className="mb-3">
                <label htmlFor="twitterCard" className="form-label">
                  Twitter Card Type
                </label>
                <select
                  className="form-select"
                  id="twitterCard"
                  name="twitterCard"
                  value={formData.twitterCard}
                  onChange={handleInputChange}
                >
                  <option value="summary">Summary</option>
                  <option value="summary_large_image">
                    Summary Large Image
                  </option>
                  <option value="app">App</option>
                  <option value="player">Player</option>
                </select>
              </div>

              <div className="mb-3">
                <label htmlFor="twitterTitle" className="form-label">
                  Twitter Title
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="twitterTitle"
                  name="twitterTitle"
                  value={formData.twitterTitle}
                  onChange={handleInputChange}
                  placeholder="Twitter Title"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="twitterDescription" className="form-label">
                  Twitter Description
                </label>
                <textarea
                  className="form-control"
                  id="twitterDescription"
                  name="twitterDescription"
                  rows="3"
                  value={formData.twitterDescription}
                  onChange={handleInputChange}
                  placeholder="Twitter Description"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="twitterImage" className="form-label">
                  Twitter Image URL
                </label>
                <input
                  type="url"
                  className="form-control"
                  id="twitterImage"
                  name="twitterImage"
                  value={formData.twitterImage}
                  onChange={handleInputChange}
                  placeholder="https://example.com/twitter-image.jpg"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : seoSetting ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SEOSettingsForm;
